import React, { useRef } from 'react';
import { animated, useSpring } from 'react-spring';
import _merge from 'lodash/merge';
import ChevronDownIcon from '@nokia-csf-uxr/ccfk-assets/ChevronDownIcon';
import { getContext, isAnimationDisabled, KEY_CODES } from 'common';

import IconButton from 'components/IconButton';
import Typography from 'components/Typography';

import Styled from './styles';

const SPRING_CONFIG = { mass: 5, tension: 80, friction: 10, clamp: true, unique: true };

const isValidKey = event => (event.key === KEY_CODES.ENTER_KEY || event.key === KEY_CODES.SPACE_KEY);

interface ExpansionButtonProps extends React.ComponentPropsWithRef<'div'> {
  /** Set `true`, to render the expanded state. */
  expanded: boolean;
  /** Callback fired when the state is changed. Signature: `function(boolean) => void`. */
  onExpansionChange: (args: boolean) => void;
  /** Title of the collapsed state. */
  collapsedTitle?: React.ReactNode;
  /** Title of the expanded state. */
  expandedTitle?: React.ReactNode;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
  /** If true, disable all motion animation for this component.  This property will override any global disableAnimation setting. */
  disableAnimation?: boolean;
}

/** ExpansionButton, renders an icon and a title for expanded and collapsed states. Other props will be provided to the root element. To get the ref of the outermost element use `ref` prop. [Read React useRef hook documentation](https://reactjs.org/docs/hooks-reference. */
const ExpansionButton = React.forwardRef<HTMLElement, ExpansionButtonProps>((props: ExpansionButtonProps, ref) => getContext(({ RTL, theme, disableAnimation }) => {
  const iconButtonRef = useRef(undefined);
  const {
    expanded,
    theme: themeProps,
    onExpansionChange,
    expandedTitle,
    collapsedTitle,
    disableAnimation: propsdisableAnimation,
    ...otherProps
  } = props;
  const resolvedTheme = _merge({}, theme.EXPANSION_BUTTON, themeProps);
  const expandButtonStyle = useSpring({
    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
    config: SPRING_CONFIG,
    immediate: isAnimationDisabled(propsdisableAnimation, disableAnimation),
  });
  return (
    <Styled.ExpansionButton
      ref={ref}
      $rtl={RTL}
      onClick={() => { onExpansionChange(!expanded); }}
      onKeyPress={(event) => { isValidKey(event) && onExpansionChange(!expanded); }}
      role="button"
      tabIndex={-1}
      onFocus={() => iconButtonRef && iconButtonRef.current && iconButtonRef.current.focus()}
      {...otherProps}
    >
      <Typography theme={resolvedTheme} variant="CAPTION">{expanded ? expandedTitle : collapsedTitle}</Typography>
      <Styled.ExpansionIconWrapper
        $rtl={RTL}
        as={animated.div}
        style={expandButtonStyle}
      >
        <IconButton
          ref={iconButtonRef}
          onClick={(event) => { event.stopPropagation(); onExpansionChange(!expanded); }}
          onKeyDown={(event) => { isValidKey(event) && onExpansionChange(!expanded); }}
        >
          <ChevronDownIcon />
        </IconButton>
      </Styled.ExpansionIconWrapper>
    </Styled.ExpansionButton>
  );
}));

ExpansionButton.displayName = 'ExpansionButton';

ExpansionButton.defaultProps = {
  collapsedTitle: 'Show Detail',
  expandedTitle: 'Hide Detail',
  theme: {},
  disableAnimation: undefined,
};

export { ExpansionButton as default, ExpansionButtonProps };